import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { useLang, useL } from "@/i18n";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/shared";

export default function Shop() {
  const { t, lang } = useLang();
  const L = useL();
  const [params, setParams] = useSearchParams();
  const [cats, setCats] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const category = params.get("category") || "";
  const search = params.get("search") || "";
  const tag = params.get("tag") || "";
  const sort = params.get("sort") || "featured";

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (search) qs.set("search", search);
    if (tag) qs.set("tag", tag);
    qs.set("sort", sort);
    qs.set("limit", "48");
    api.get(`/products?${qs}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [category, search, tag, sort]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const activeCategory = cats.find((x) => x.slug === category);
  const activeCatName = activeCategory ? L(activeCategory, "name") : t("shop.all");

  return (
    <div data-testid="shop-page">
      <div className="border-b border-border bg-cream-card">
        <div className="mx-auto max-w-[1400px] px-4 py-8">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sunset">Kreyola Boutik</p>
          <h1 className="mt-2 font-display text-4xl font-bold text-navy">{search ? `"${search}"` : activeCatName || t("shop.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{data.total} {lang === "fr" ? "produits" : "products"}</p>
        </div>
      </div>

      <div className="mx-auto grid max-w-[1400px] gap-8 px-4 py-8 lg:grid-cols-[240px_1fr]">
        {/* Sidebar */}
        <aside className={`${filtersOpen ? "fixed inset-0 z-[70] overflow-y-auto bg-cream p-5" : "hidden"} lg:static lg:block lg:p-0`}>
          <div className="mb-4 flex items-center justify-between lg:hidden">
            <h3 className="font-display text-xl font-bold text-navy">{t("shop.filters")}</h3>
            <button onClick={() => setFiltersOpen(false)}><X /></button>
          </div>
          <div className="rounded-2xl border border-border bg-cream-card p-5">
            <h4 className="mb-3 font-head text-sm font-bold uppercase tracking-wider text-navy">{t("shop.category")}</h4>
            <ul className="space-y-1">
              <li><button onClick={() => { setParam("category", ""); setFiltersOpen(false); }} className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${!category ? "bg-gold/20 font-semibold text-navy" : "text-navy/70 hover:bg-cream"}`} data-testid="filter-cat-all">{t("shop.all")}</button></li>
              {cats.map((c) => (
                <li key={c.id}>
                  <button onClick={() => { setParam("category", c.slug); setFiltersOpen(false); }} data-testid={`filter-cat-${c.slug}`}
                    className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${category === c.slug ? "bg-gold/20 font-semibold text-navy" : "text-navy/70 hover:bg-cream"}`}>
                    {L(c, "name")}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Products */}
        <div>
          <div className="mb-6 flex items-center justify-between gap-3">
            <button onClick={() => setFiltersOpen(true)} className="flex items-center gap-2 rounded-full border border-border bg-cream-card px-4 py-2 text-sm font-semibold text-navy lg:hidden" data-testid="open-filters-btn">
              <SlidersHorizontal size={15} /> {t("shop.filters")}
            </button>
            <select value={sort} onChange={(e) => setParam("sort", e.target.value)} data-testid="sort-select" className="ml-auto rounded-full border border-border bg-cream-card px-4 py-2 text-sm font-medium text-navy outline-none">
              <option value="featured">{t("shop.rating")}</option>
              <option value="price_asc">{t("shop.price")} ↑</option>
              <option value="price_desc">{t("shop.price")} ↓</option>
              <option value="newest">{t("shop.newest")}</option>
            </select>
          </div>

          {loading ? (
            <p className="py-16 text-center text-muted-foreground">{t("common.loading")}</p>
          ) : data.items.length === 0 ? (
            <p className="py-16 text-center text-muted-foreground" data-testid="no-results">{t("shop.noResults")}</p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
              {data.items.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
